import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Check } from 'lucide-react';
import { billingApi, type BillingInfo } from '../services/api';
import { PRICING_CONFIG, PLANS, PRO_ANNUAL_PRICE_PER_USER } from '../config/pricing';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  feature?: string;
  title?: string;
  description?: string;
}

export default function UpgradeModal({
  isOpen,
  onClose,
  companyId,
  feature = 'AI Features',
  title,
  description,
}: UpgradeModalProps) {
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  useEffect(() => {
    if (isOpen && companyId) {
      loadBillingInfo();
    }
  }, [isOpen, companyId]);

  const loadBillingInfo = async () => {
    try {
      setIsLoading(true);
      const response = await billingApi.getBillingInfo(companyId);

      if (response.success && response.data) {
        setBillingInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to load billing info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFounderCheckout = async () => {
    try {
      setIsLoading(true);
      const response = await billingApi.createFounderCheckoutSession(companyId);

      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to create Founder checkout:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProCheckout = async () => {
    try {
      setIsLoading(true);
      const response = await billingApi.createCheckoutSession(companyId, {
        billingCycle,
        userCount: 5, // Pro is per company, not per user
      });

      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to create Pro checkout:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Determine which upgrade path to show
  const isOnFreeTier = billingInfo?.plan === 'free';
  const isOnFounderTier = billingInfo?.plan === 'founder';

  const founderConfig = PRICING_CONFIG[PLANS.FOUNDER];
  const proConfig = PRICING_CONFIG[PLANS.PRO];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg">
                  <Crown className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {title || (isOnFreeTier ? `Unlock ${feature}` : `Upgrade to ${feature}`)}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {description ||
                      (isOnFreeTier
                        ? 'Get started with Founder to access strategic planning tools.'
                        : 'Activate Pro for full execution capabilities.')}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Founder Upgrade (for free tier users) */}
            {isOnFreeTier && (
              <div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{founderConfig.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{founderConfig.tagline}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-baseline gap-1.5 justify-end">
                        <span className="text-base font-medium text-gray-400 line-through">
                          ${founderConfig.price.standard.amount}
                        </span>
                        <span className="text-2xl font-bold text-gray-900">
                          ${founderConfig.price.launch?.amount}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">/year · Year 1</div>
                      <div className="text-xs text-gray-400">
                        renews at ${founderConfig.price.standard.amount}
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-4">
                    {founderConfig.features.included.slice(0, 6).map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <Check className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={handleFounderCheckout}
                    disabled={isLoading}
                    className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {isLoading ? 'Loading...' : 'Subscribe to Founder'}
                  </button>
                </div>
              </div>
            )}

            {/* Pro Activation Upgrade (for founder tier users) */}
            {isOnFounderTier && (
              <div>
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{proConfig.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{proConfig.tagline}</p>
                    </div>
                  </div>

                  {/* Billing Cycle Toggle */}
                  <div className="flex items-center justify-center space-x-1 bg-white rounded-lg p-1 mb-4">
                    <button
                      onClick={() => setBillingCycle('monthly')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        billingCycle === 'monthly'
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setBillingCycle('yearly')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        billingCycle === 'yearly'
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Yearly
                    </button>
                  </div>

                  <div className="text-center mb-4">
                    <div className="flex items-baseline justify-center gap-3">
                      <span className="text-xl font-medium text-gray-400 line-through">
                        $
                        {billingCycle === 'yearly'
                          ? (PRO_ANNUAL_PRICE_PER_USER.standard * 5).toLocaleString()
                          : (proConfig.price.standard.amount * 5).toLocaleString()}
                      </span>
                      <span className="text-3xl font-bold text-gray-900">
                        $
                        {billingCycle === 'yearly'
                          ? (240 * 5).toLocaleString()
                          : (20 * 5).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      for 5 users per {billingCycle === 'yearly' ? 'year' : 'month'} · Year 1
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      ${billingCycle === 'yearly' ? 240 : 20}/user · renews at $
                      {billingCycle === 'yearly'
                        ? PRO_ANNUAL_PRICE_PER_USER.standard
                        : proConfig.price.standard.amount}
                      /user
                    </div>
                  </div>

                  <ul className="space-y-2 mb-4">
                    {proConfig.features.included.slice(0, 6).map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <Check className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={handleProCheckout}
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {isLoading ? 'Loading...' : 'Activate Pro'}
                  </button>
                </div>
              </div>
            )}

            {/* Both plans for reference (collapsed view) */}
            {isOnFreeTier && (
              <div className="border-t pt-6">
                <p className="text-sm text-gray-600 mb-3">
                  Need full execution capabilities? Upgrade to Pro Activation after Founder.
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">Pro Activation</p>
                      <p className="text-xs text-gray-600">Requires Founder subscription</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-baseline gap-1.5 justify-end">
                        <span className="text-sm text-gray-400 line-through">
                          ${proConfig.price.standard.amount}/user/mo
                        </span>
                        <span className="font-bold text-gray-900">$20/user/mo</span>
                      </div>
                      <p className="text-xs text-gray-400">
                        Year 1 · renews at ${proConfig.price.standard.amount} · Min 5 users
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="text-center text-xs text-gray-500 space-y-1 pt-4 border-t">
              <p>No setup fees • Cancel anytime</p>
              <p>Secure checkout powered by Stripe</p>
              <p className="text-amber-700 font-medium">
                Launch pricing applies with coupon code at checkout
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
